// One-time conversion of legacy "GeoSketch" data (FormAnswerSketch.sketchJson — lat/lng
// polygon "layers" classified via SketchArea/SketchSubArea/SketchSubAreaOption) into
// TapeSketchFeature-shaped objects (local feet vertices, classified via
// tapeFeatureTypeId/tapeFoundationTypeId/tapeGarageKindId), for orders that were sketched
// before the Tape tool existed.
//
// `layerData` must be the real, live SketchArea config (GET orderreview/sketch/config —
// the same data the old editing UI already uses) so old area/subarea/option ids are
// resolved by NAME rather than trusting hardcoded ids, which can vary by environment.
// The old area/subarea names below were sourced from a frontend mock, not a verified
// production dump — spot-check converted sketches against a few real orders before
// trusting this broadly, and adjust the tables below if a real name doesn't match.
//
// This intentionally only produces a DRAFT: the caller seeds `tapeFeatures` with the
// result, which flows through the exact same auto-save-as-draft (isComplete: false) path
// as a hand-drawn Tape sketch — QA still has to review and explicitly complete it.
import { latLngToFeet } from './geo';
import { measureSqft, GARAGE_KIND_DEFAULT_ABOVE, uid } from './tapeUtils';

// Old SketchArea.Name -> Tape feature type Code. Any area not listed here (Breezeway,
// Passageway, Sunroom) has no Tape equivalent and falls back to OTH — the footprint is
// kept, just tagged generically, rather than dropped.
const AREA_NAME_TO_TYPE_CODE = {
  'story height': 'DWL',
  garage: 'GAR',
  porch: 'POR',
  deck: 'DK',
  patio: 'PAT',
};

// Old foundation SketchSubAreaOption.Name (nested under the dwelling's story-count
// SubArea) -> Tape TapeFoundationType.Code. Note "Crawlspace" -> CR ("Crawl"), not the
// old system's own (colliding-with-Pier) code.
const FOUNDATION_NAME_TO_CODE = {
  slab: 'S',
  crawlspace: 'CR',
  crawl: 'CR',
  basement: 'B',
  pier: 'P',
};

// Old garage SketchSubArea.Name -> Tape TapeGarageKind.Name. Tape has no "Attached" or
// "Carport" kind, so both fall back to Standard.
const GARAGE_SUBAREA_NAME_TO_KIND_NAME = {
  'attached garage': 'Standard',
  carport: 'Standard',
  'built-in': 'Built-in',
  basement: 'Basement',
};

const norm = s => (s || '').trim().toLowerCase();

const findByCode = (list, code) => (list || []).find(x => x.code === code);
const findByName = (list, name) => {
  const n = norm(name);
  return (list || []).find(x => norm(x.name) === n);
};

// Old layers sometimes repeat the first vertex at the end to explicitly close the ring;
// the Tape tool stores an open ring and closes it implicitly, so drop the duplicate.
function openRing(latLngs) {
  if (!latLngs || latLngs.length < 2) {
    return latLngs || [];
  }
  const first = latLngs[0];
  const last = latLngs[latLngs.length - 1];
  const closed = Math.abs(first.lat - last.lat) < 1e-9 && Math.abs(first.lng - last.lng) < 1e-9;
  return closed ? latLngs.slice(0, -1) : latLngs;
}

function resolveTypeCode(areaName, subAreaName) {
  const area = norm(areaName);
  if (area.includes('architect')) {
    // "Architectural/Architectual Features" area: only Balcony has a Tape equivalent
    // (Passageway/Sunroom fall back to OTH below).
    return norm(subAreaName).includes('balcony') ? 'BAL' : 'OTH';
  }
  return AREA_NAME_TO_TYPE_CODE[area] || 'OTH';
}

/**
 * @param {object} sketchJson - parsed FormAnswerSketch.sketchJson
 * @param {Array} layerData - real SketchArea config, from GET orderreview/sketch/config
 * @param {object} tapeConfig - Tape lookup config, from GET tapesketch/config
 * @returns {Array} TapeSketchFeature-shaped objects (features not yet persisted; no tapeSketchId)
 */
export function convertGeoSketchToTapeFeatures(sketchJson, layerData, tapeConfig) {
  const origin = sketchJson?.center;
  const layers = sketchJson?.layers || [];
  if (!origin || !layers.length || !tapeConfig) {
    return [];
  }

  const shapeLayers = layers.filter(l => l.layerType !== 'baseLayer' && Array.isArray(l.latLngs) && l.latLngs.length >= 3);

  const features = [];
  shapeLayers.forEach((layer, index) => {
    const area = (layerData || []).find(a => a.sketchAreaId === layer.sketchAreaId);
    const subArea = area?.subArea?.find(s => s.sketchSubAreaId === layer.sketchSubAreaId);
    const option = subArea?.options?.find(o => o.sketchSubAreaOptionId === layer.sketchSubAreaOptionId);

    const typeCode = resolveTypeCode(area?.name, subArea?.name);
    const featureType = findByCode(tapeConfig.featureTypes, typeCode) || findByCode(tapeConfig.featureTypes, 'OTH');
    if (!featureType) {
      return;
    }

    let stories = 1;
    let storiesAbove;
    let tapeFoundationTypeId;
    let tapeGarageKindId;

    if (typeCode === 'DWL') {
      const parsedStories = parseFloat(subArea?.name ?? subArea?.description);
      stories = Number.isFinite(parsedStories) && parsedStories > 0 ? parsedStories : 1;
      const foundationCode = FOUNDATION_NAME_TO_CODE[norm(option?.name)];
      const foundation = (foundationCode && findByCode(tapeConfig.foundationTypes, foundationCode)) || findByCode(tapeConfig.foundationTypes, 'S');
      tapeFoundationTypeId = foundation?.id;
    } else if (typeCode === 'GAR') {
      const kindName = GARAGE_SUBAREA_NAME_TO_KIND_NAME[norm(subArea?.name)] || 'Standard';
      const kind = findByName(tapeConfig.garageKinds, kindName) || findByName(tapeConfig.garageKinds, 'Standard');
      tapeGarageKindId = kind?.id;
      storiesAbove = GARAGE_KIND_DEFAULT_ABOVE[kindName] ?? 0;
    } else if (typeCode === 'POR' || typeCode === 'BAL') {
      storiesAbove = norm(subArea?.name).includes('two story') ? 1 : 0;
    }

    const vertices = openRing(layer.latLngs)
      .map(ll => latLngToFeet(origin, ll))
      .map(v => ({ x: Math.round(v.x), y: Math.round(v.y) }));
    if (vertices.length < 3) {
      return;
    }

    const footprintSqft = Math.round(measureSqft(vertices));
    if (footprintSqft <= 0) {
      return;
    }
    const livingSqftVal =
      typeCode === 'DWL'
        ? Math.round(footprintSqft * stories)
        : typeCode === 'GAR' || typeCode === 'POR' || typeCode === 'BAL'
          ? Math.round(footprintSqft * (storiesAbove ?? 0))
          : 0;

    features.push({
      id: uid(),
      tapeFeatureTypeId: featureType.id,
      tapeFoundationTypeId,
      tapeGarageKindId,
      stories,
      storiesAbove,
      isCathedral: false,
      footprintSqft,
      livingSqft: livingSqftVal,
      vertices,
      sortOrder: index,
    });
  });

  return features;
}
